/**
 * QL-287 — Cash Transfers (advisor entry). The list is instance-wide, so every
 * placement edits the same transfers; each saved change refreshes the list and
 * the Account Cash Overview wherever it is placed.
 */

import type { CreateCashTransferRequest } from "@repo/shared/types";
import { apiErrorMessage } from "@/lib/api/_client";
import {
  useCashTransfers,
  useCreateCashTransfer,
  useDeleteCashTransfer,
  useUpdateCashTransfer,
} from "@/lib/hooks/use-flow-instances/use-cash";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { CashTransfersConfig } from "@/lib/types/flow";
import { T } from "./cash-transfers.i18n";
import { CashTransfersView } from "./cash-transfers-view";

type CashTransfersProps = {
  instanceId: string;
  config: CashTransfersConfig;
  disabled: boolean;
};

export function CashTransfers({ instanceId, config, disabled }: CashTransfersProps) {
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();
  const { data, isLoading, error } = useCashTransfers(instanceId);
  const create = useCreateCashTransfer(instanceId);
  const update = useUpdateCashTransfer(instanceId);
  const remove = useDeleteCashTransfer(instanceId);

  if (isLoading) return null;
  if (error || !data) {
    return <p className="text-sm text-destructive">{t.failedToLoad}</p>;
  }

  const run = async (action: () => Promise<unknown>, fallback: string) => {
    try {
      await action();
      return { success: true };
    } catch (err) {
      return { success: false, error: apiErrorMessage(err, fallback) };
    }
  };

  return (
    <CashTransfersView
      instanceId={instanceId}
      data={data}
      config={config}
      disabled={disabled}
      isPending={create.isPending || update.isPending || remove.isPending}
      fmt={formatNumber}
      onCreate={(input: CreateCashTransferRequest) =>
        run(() => create.mutateAsync(input), t.failedToSave)
      }
      onUpdate={(transferId, input) =>
        run(() => update.mutateAsync({ transferId, data: input }), t.failedToSave)
      }
      onDelete={(transferId) => run(() => remove.mutateAsync(transferId), t.failedToDelete)}
    />
  );
}
